import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Period =
  | { kind: "month"; year: number; month: number } // month: 0-11
  | { kind: "year"; year: number }
  | { kind: "all" };

const STORAGE_KEY = "pccrm.period";

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];
const MONTHS_FR_LONG = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function currentMonthPeriod(): Period {
  const now = new Date();
  return { kind: "month", year: now.getFullYear(), month: now.getMonth() };
}

function loadPeriod(): Period {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Period;
      if (p && (p.kind === "all" || p.kind === "year" || p.kind === "month")) {
        return p;
      }
    }
  } catch {
    /* ignore malformed value */
  }
  return currentMonthPeriod();
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// Inclusive [from, to] bounds as YYYY-MM-DD; null for "all".
function computeBounds(p: Period): { from: string; to: string } | null {
  if (p.kind === "all") return null;
  if (p.kind === "year") {
    return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  }
  const from = `${p.year}-${pad2(p.month + 1)}-01`;
  const lastDay = new Date(p.year, p.month + 1, 0).getDate();
  const to = `${p.year}-${pad2(p.month + 1)}-${pad2(lastDay)}`;
  return { from, to };
}

function periodLabel(p: Period): string {
  if (p.kind === "all") return "Tout";
  if (p.kind === "year") return String(p.year);
  return `${MONTHS_FR_LONG[p.month]} ${p.year}`;
}

type PeriodContextValue = {
  period: Period;
  setPeriod: (p: Period) => void;
  inPeriod: (dateISO: string | null | undefined) => boolean;
  bounds: () => { from: string; to: string } | null;
  label: () => string;
};

const PeriodContext = createContext<PeriodContextValue | null>(null);

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [period, setPeriodState] = useState<Period>(loadPeriod);

  const setPeriod = useCallback((p: Period) => {
    setPeriodState(p);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* ignore quota/availability errors */
    }
  }, []);

  const bounds = useMemo(() => computeBounds(period), [period]);

  const inPeriod = useCallback(
    (dateISO: string | null | undefined): boolean => {
      if (!bounds) return true; // "all"
      if (!dateISO) return false;
      const d = dateISO.slice(0, 10);
      return d >= bounds.from && d <= bounds.to;
    },
    [bounds],
  );

  const value = useMemo<PeriodContextValue>(
    () => ({
      period,
      setPeriod,
      inPeriod,
      bounds: () => bounds,
      label: () => periodLabel(period),
    }),
    [period, setPeriod, inPeriod, bounds],
  );

  return (
    <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>
  );
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriod must be used within PeriodProvider");
  return ctx;
}

export { MONTHS_FR };
